// Vendor
import * as React from 'react';
import { inject } from 'mobx-react';

// Bernie
import { AnalyticsStore, ExperimentStore, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from 'bernie-view';

// Lodging Stores
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { SearchResultsStore } from 'stores/search-results-store';
import { ShortlistStore } from 'stores/shortlist-store';

// Models
import { GalleryViewModel, HotelViewModel, PropertyListingBadgeModel } from 'models';

// UITK
import { UitkFigureAspectRatioType } from 'uitk-react-images';

// Utils
import { onClickScrollToTarget } from 'utils/smooth-scroll';

// Internals
import { DefaultListing } from './internals/default-listing';
import { HorizontalListing } from './internals/horizontal-listing';
import { Title } from './internals/title';

export interface ListingComponentProps {
  analytics?: AnalyticsStore;
  breakfastIncluded?: string;
  className: string;
  context?: Context;
  currentHotel?: CurrentHotelStore;
  'data-stid'?: string;
  dataAttributes?: any;
  endDate?: string;
  index?: number;
  isDatelessPdp?: boolean;
  gallery?: GalleryViewModel;
  lazyLoadImg?: boolean;
  loading?: boolean;
  searchResults?: SearchResultsStore;
  stars?: string;
  startDate?: string;
  source?: string;
  model: HotelViewModel;
  referralObject?: {
    navigation?: {
      linkName?: string;
      rfrr?: string;
    };
    favorite?: {
      linkName?: string;
      rfrr?: string;
    };
    unfavorite?: {
      linkName?: string;
      rfrr?: string;
    };
    close?: {
      linkName?: string;
      rfrr?: string;
    };
  };
  routeStore?: RouteStore;
  shortlist?: ShortlistStore;
  displayViewed?: boolean;
  experiment?: ExperimentStore;
  setResultRef?: (ref: HTMLElement, index: number) => void;
  defaultListingOnly?: boolean;
  disableGallery?: boolean;
  mapListingView?: boolean;
  onFavoriteClick?: () => void;
  excludePriceBreakDownLink?: boolean;
}

interface ListingComponentState {
  alternateDatesExpanded: boolean;
}

@inject('routeStore', 'analytics', 'currentHotel')
class ListingComponent extends React.Component<ListingComponentProps, ListingComponentState> {
  constructor(props) {
    super(props);

    this.listingClickHandler = this.listingClickHandler.bind(this);
    this.setItemRef = this.setItemRef.bind(this);
    this.handleAdditionalInfoClick = this.handleAdditionalInfoClick.bind(this);
    this.routeStoreGoBack = this.routeStoreGoBack.bind(this);
  }

  private static alternateDatesInfo = {
    collapsedLinkName: 'HSR show alt dates',
    collapsedMsgKey: 'hotels.searchResults.alternateDates.more',
    collapsedRfrr: 'HOT:SR:ShowAltDates',
    expandedLinkName: 'HSR hide alt dates',
    expandedMsgKey: 'hotels.searchResults.alternateDates.less',
    expandedRfrr: 'HOT:SR:HideAltDates',
    headerTextKey: 'hotels.searchResults.alternateDates.try'
  };

  private handleAdditionalInfoClick() {
    onClickScrollToTarget({ scrollTo: 0 });
  }

  private setItemRef(ref) {
    const { index, setResultRef } = this.props;
    if (setResultRef && typeof setResultRef === 'function') {
      setResultRef(ref, index);
    }
  }

  private listingClickHandler(): void {
    const { currentHotel, model, searchResults } = this.props;
    const id = model.id;
    let hotel;
    if (searchResults && searchResults.hotels && searchResults.hotels.length > 0) {
      hotel = searchResults.hotels.find(h => h.id === id);
    }
    if (model && model.travelAd) {
      const travelAd = model.travelAd;
      if (travelAd.clickRedirectLinkoffTrackingUrl) {
        window.open(travelAd.clickRedirectLinkoffTrackingUrl);
      }
    }

    if (currentHotel && currentHotel.setSearchResultAsCurrentHotel && hotel) {
      currentHotel.setSearchResultAsCurrentHotel(hotel);

      return;
    }
  }

  private getTitleComponent(imageRatio = UitkFigureAspectRatioType.R21_9, extraProps = {}) {
    const {
      index,
      lazyLoadImg,
      model,
      referralObject,
      displayViewed,
      disableGallery,
      isDatelessPdp,
      onFavoriteClick,
      mapListingView
    } = this.props;

    const {
      id,
      propertyListingBadgeModel,
      infositeUrl,
      infositeDatelessUrl,
      name,
      neighborhood,
      pinnedDetails,
      price,
      reviews,
      shortlistModel,
      starRatingIcon,
      thumbnail,
      travelAd
    } = model;
    const stars = isDatelessPdp ? model.comparisonModel && model.comparisonModel.starRating : model.stars;

    const { analytics, currentHotel } = this.props;
    let gallery = null;
    if (!disableGallery) {
      gallery = {
        images: model && model.gallery ? model.gallery.images : []
      };
    }
    const titleProps = {
      analytics,
      currentHotel,
      channel: pinnedDetails && pinnedDetails.channelName,
      className: 'listing__title',
      propertyListingBadgeModel,
      imageUrl: (thumbnail && thumbnail.src) || '',
      fallbackImageUrl: (thumbnail && thumbnail.fallBackImage) || '',
      gallery,
      index,
      lazyLoadImg,
      name,
      pinned: pinnedDetails,
      neighborhood,
      stars,
      starRatingIcon,
      travelAd,
      shortlistModel,
      infositeUrl: isDatelessPdp ? infositeDatelessUrl : infositeUrl,
      thumbnail,
      reviews,
      price,
      hotelId: id,
      referralObject,
      displaySecondaryBadge: !isDatelessPdp,
      displayViewed,
      ratio: imageRatio,
      listingClickHandler: this.listingClickHandler,
      onFavoriteClick,
      mapListingView
    };

    return <Title {...titleProps} {...extraProps} />;
  }

  private routeStoreGoBack() {
    this.props.routeStore.goBack();
  }

  private getDefaultListingTopMarginClass(propertyListingBadgeModel: PropertyListingBadgeModel): string {
    const secondaryBadgeIconExists =
      propertyListingBadgeModel &&
      propertyListingBadgeModel.secondary &&
      (propertyListingBadgeModel.secondary.icon || propertyListingBadgeModel.secondary.text);

    return secondaryBadgeIconExists ? 'all-t-margin-half' : '';
  }

  public render() {
    const {
      className,
      defaultListingOnly,
      index,
      loading,
      model,
      routeStore,
      isDatelessPdp,
      mapListingView,
      onFavoriteClick,
      excludePriceBreakDownLink
    } = this.props;
    const { alternateDates, propertyListingBadgeModel, infositeUrl, infositeDatelessUrl } = model;
    const lowerRight = propertyListingBadgeModel && propertyListingBadgeModel.lowerRight;
    const defaultListingTopMarginClass = this.getDefaultListingTopMarginClass(propertyListingBadgeModel);
    const stars = isDatelessPdp ? model.comparisonModel && model.comparisonModel.starRating : model.stars;

    const alternateDatesProps = {
      handleClick: this.handleAdditionalInfoClick,
      model: alternateDates
    };

    const cardLinkProps = { ...this.props };
    cardLinkProps.model.infositeUrl = isDatelessPdp ? infositeDatelessUrl : infositeUrl;

    const commonListingProps = {
      alternateDatesInfo: ListingComponent.alternateDatesInfo,
      alternateDatesProps,
      index,
      loading,
      model,
      routeStore,
      isDatelessPdp,
      className,
      lowerRight,
      stars,
      getTitleComponent: this.getTitleComponent.bind(this),
      dataStid: this.props['data-stid'],
      cardLinkProps,
      routeStoreGoBack: this.routeStoreGoBack.bind(this),
      listingClickHandler: this.listingClickHandler.bind(this),
      setItemRef: this.setItemRef.bind(this),
      mapListingView,
      onFavoriteClick,
      excludePriceBreakDownLink
    };

    const defaultListingProps = {
      ...commonListingProps,
      defaultListingTopMarginClass
    };

    if (defaultListingOnly) {
      return <DefaultListing {...defaultListingProps} />;
    }

    return (
      <Viewport>
        <ViewSmall>
          <DefaultListing {...defaultListingProps} />
        </ViewSmall>
        <ViewMedium>
          <HorizontalListing {...commonListingProps} imageAspectRatio={`${UitkFigureAspectRatioType.R3_2}`} />
        </ViewMedium>
        <ViewLarge>
          <HorizontalListing {...commonListingProps} imageAspectRatio={`${UitkFigureAspectRatioType.R16_9}`} />
        </ViewLarge>
      </Viewport>
    );
  }
}

export const Listing = inject('searchResults', 'currentHotel', 'context', 'shortlist', 'analytics', 'experiment')(ListingComponent);
